import { memo } from "react";
import type { InstanceDetail } from "@keydials/api-client";
import { dialBarParts, type DialVM } from "../dialModel";
import { DialView } from "./DialView";
import { AvatarStack, StatusBar } from "./primitives";
import { Icon } from "./Icon";
import { personFrom } from "../lib/ui";

/** The most recent status change across a dial (segment or item), or the fallback when nothing's set. */
export function latestUpdate(detail: InstanceDetail, fallback: string): string {
  const stamps = [
    ...detail.statuses.map((s) => s.updatedAtUtc),
    ...(detail.itemStatuses ?? []).map((s) => s.updatedAtUtc),
  ];
  return stamps.length ? stamps.reduce((a, b) => (a > b ? a : b)) : fallback;
}

/**
 * A portfolio card for a single dial. The owning organisation is the bold title with the definition
 * name as a subtitle; the ring is the hero (full width), with the completion bar and members below.
 *
 * Memoised (KEY-195): the My Dials page holds each card's data in stable state objects, so typing in
 * the search box — which re-renders the dashboard — no longer recomputes every card's dial SVG. The
 * comparator ignores `onOpen`: each card's handler always navigates to its own (stable) route, so a
 * fresh inline closure each render is harmless.
 */
export const DialCard = memo(function DialCard({
  vm,
  name,
  org,
  definitionName,
  updated,
  testing = false,
  demo = false,
  needsSetup = false,
  onOpen,
}: {
  vm: DialVM | null;
  name: string;
  org: string;
  definitionName: string;
  updated: string;
  /** True when this dial only runs a version out for checking (KEY-283) — a throwaway test dial. */
  testing?: boolean;
  /** True for a demo dial (KEY-938) — flagged on the card so the affiliate can tell it from their real one. */
  demo?: boolean;
  /** True when the dial asks for something before it will open (KEY-434) — so the card says why it is
   * blank rather than sitting as a permanent skeleton. Opening it lands on the capture screen. */
  needsSetup?: boolean;
  onOpen: () => void;
}) {
  const pct = vm ? Math.round(vm.completion * 100) : 0;
  return (
    <button
      onClick={onOpen}
      className="kd-card kd-card-hover kd-focusable"
      style={{
        padding: "var(--pad-card)",
        textAlign: "left",
        cursor: "pointer",
        display: "flex",
        flexDirection: "column",
        gap: "var(--gap-stack)",
        font: "inherit",
        color: "inherit",
        width: "100%",
      }}
    >
      {/* Header: the owning organisation (bold) with the definition title beneath, along the top of the card. */}
      <div>
        <div className="display" style={{ fontSize: 19, fontWeight: 700, color: "var(--text)", lineHeight: 1.2 }}>
          {org || name}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 7, marginTop: 3, flexWrap: "wrap" }}>
          <span style={{ fontSize: 13, color: "var(--text-faint)", fontWeight: 500 }}>{definitionName}</span>
          {/* Flagged on the card as well as inside, so a test dial is recognisable before it's opened. */}
          {testing && (
            <span
              style={{
                fontSize: 10.5, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase",
                color: "#fff", background: "var(--st-risk, #7a3b9c)", borderRadius: 999, padding: "1px 7px",
              }}
              title="This dial is being tested. Anything entered into it is deleted when testing finishes."
            >
              Testing
            </span>
          )}
          {demo && (
            <span
              style={{
                fontSize: 10.5, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase",
                color: "#fff", background: "var(--st-info, #2563eb)", borderRadius: 999, padding: "1px 7px",
              }}
              title="This is a demo dial. Files are never stored in it, and everything in it is made up."
            >
              Demo
            </span>
          )}
          {needsSetup && (
            <span
              style={{
                fontSize: 10.5, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase",
                // Dark on amber rather than white: the status hues are fixed across themes (see theme.css),
                // and white on this one is barely legible. Amber, not the Testing badge's orange — this is
                // a to-do, not a warning about the dial's nature.
                color: "#3a2a00", background: "var(--st-progress)", borderRadius: 999, padding: "1px 7px",
              }}
              title="This dial needs a few details before it can be opened."
            >
              Needs setting up
            </span>
          )}
        </div>
      </div>

      {/* The ring is the hero: full width (the card padding is its margin), with no text at its sides. */}
      {vm && (
        <div style={{ width: "100%", aspectRatio: "1 / 1", margin: "0 auto" }}>
          <DialView vm={vm} interactive={false} showLabels={false} />
        </div>
      )}

      {vm && (
        <div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: 7,
              fontSize: 12.5,
              fontWeight: 600,
              color: "var(--text-muted)",
            }}
          >
            <span>
              {vm.done} of {vm.total} complete
            </span>
            <span style={{ color: "var(--text)" }}>{pct}%</span>
          </div>
          <StatusBar parts={dialBarParts(vm)} />
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <AvatarStack people={(vm?.detail.members ?? []).map((m) => personFrom(m))} size={28} />
        {updated && (
          <span style={{ fontSize: 12, color: "var(--text-faint)", display: "inline-flex", alignItems: "center", gap: 5, fontWeight: 500 }}>
            <Icon name="clock" size={13} />
            {updated}
          </span>
        )}
      </div>
    </button>
  );
},
(prev, next) =>
  prev.vm === next.vm &&
  prev.name === next.name &&
  prev.org === next.org &&
  prev.definitionName === next.definitionName &&
  prev.updated === next.updated &&
  prev.testing === next.testing &&
  prev.demo === next.demo &&
  prev.needsSetup === next.needsSetup);
